/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ChevronDown, 
  Leaf, 
  Droplets, 
  ShieldCheck, 
  Cpu, 
  Building2, 
  TreeDeciduous, 
  Sprout, 
  LayoutGrid,
  Phone,
  User,
  Users,
  Briefcase,
  Globe,
  ArrowRight,
  ChevronRight,
  MapPin,
  Award,
  Map,
  Settings,
  Mouse,
  GraduationCap,
  Landmark,
  Factory,
  TrendingUp,
  Box,
  BarChart,
  Cloud,
  Smartphone,
  Headphones,
  FileText,
  Download
} from 'lucide-react';
import logo from '../assets/logo.png';

import imgTimber from '../assets/timber-plantation.jpeg';
import imgHorticulture from '../assets/horticulture.jpeg';
import imgSmartIrrigation from '../assets/smart-irrigation.jpg';
import imgFarmMgmt from '../assets/farm-management.jpeg';
import imgEcoSpiritual from '../assets/eco-spiritual.png';
import imgFarmhouse from '../assets/farmhouse-landscaping.PNG';
import imgLandConsult from '../assets/land-consultation.jpeg';

import imgGreenHabitat from '../assets/greenhabitat-div.png';
import imgFarm360 from '../assets/Farm360.jpg';
import imgPlantiq from '../assets/plantiQ.jpg';
import imgAgriq from '../assets/AgriQ.jpeg';
import imgIrriq from '../assets/irriQ.jpg';
import imgOrganiq from '../assets/organiQ-div.jpeg';
import imgSecure from '../assets/secure.jpeg';
import imgVedvan from '../assets/vedvan-div.PNG';


const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState(null);
  
  // Initialize from sessionStorage so the loader persists across page reloads!
  const [isTranslating, setIsTranslating] = useState(() => {
    return sessionStorage.getItem('isTranslating') === 'true';
  });
  const location = useLocation();

  const changeLanguage = (langCode) => {
    setIsTranslating(true);
    
    // 1. Try smooth translation first (usually works on desktop)
    const selectField = document.querySelector(".goog-te-combo");
    if (selectField) {
      setTimeout(() => {
        selectField.value = langCode;
        selectField.dispatchEvent(new Event("change"));
        setTimeout(() => setIsTranslating(false), 800);
      }, 50);
    } else {
      // 2. Fallback for mobile: Force cookie and reload page
      // Keep loader active across the reload using sessionStorage
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=${window.location.hostname}`;
      document.cookie = `googtrans=/en/${langCode}; path=/`; 
      sessionStorage.setItem('isTranslating', 'true');
      setTimeout(() => window.location.reload(), 50);
    }
  };

  useEffect(() => {
    // If we just reloaded the page from a mobile language change, 
    // hide the loader and clear the flag after Google Translate finishes booting up (1s)
    if (sessionStorage.getItem('isTranslating') === 'true') {
      const timer = setTimeout(() => {
        setIsTranslating(false);
        sessionStorage.removeItem('isTranslating');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { 
      name: 'Solutions', 
      path: '/solutions',
      description: 'Discover end-to-end agricultural and land management solutions tailored for sustainable, high-yield ecosystems.',
      featuredImg: imgSmartIrrigation,
      dropdown: [
        {
          category: '01 PLAN',
          categoryIcon: <Map className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Understand your land.\nDesign the right future.',
          items: [
            {
              name: 'Land Assessment & Planning',
              description: 'Detailed land, soil, water and feasibility studies with GIS mapping and master planning.',
              path: '/solutions/land-assessment-and-planning',
              icon: <LayoutGrid className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            },
            {
              name: 'Orchard & Plantation Development',
              description: 'Scientific design, premium planting material and end-to-end plantation establishment.',
              path: '/solutions/orchard-and-plantation-development',
              icon: <Sprout className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            },
            {
              name: 'Master Planning & Design',
              description: 'Integrated land use planning for farms, orchards, estates and large-scale projects.',
              path: '/solutions/master-planning-and-design',
              icon: <Globe className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            }
          ]
        },
        {
          category: '02 BUILD & OPERATE',
          categoryIcon: <Settings className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Build efficiently.\nOperate intelligently.',
          items: [
            {
              name: 'Water & Irrigation Systems',
              description: 'Smart irrigation, fertigation and water management systems for maximum efficiency and yield.',
              path: '/solutions/water-and-irrigation-systems',
              icon: <Droplets className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            },
            {
              name: 'Farm Security & Surveillance',
              description: 'Advanced security systems and real-time monitoring to protect people, assets and crops.',
              path: '/solutions/farm-security-and-surveillance',
              icon: <ShieldCheck className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            },
            {
              name: 'Farm Management Services',
              description: 'Professional management, monitoring, reporting and performance improvement services.',
              path: '/solutions/farm-management-services',
              icon: <User className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            }
          ]
        },
        {
          category: '03 RESTORE & SUSTAIN',
          categoryIcon: <Leaf className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Restore ecosystems.\nSustain for generations.',
          items: [
            {
              name: 'Ecological Restoration',
              description: 'Restore degraded lands, enhance biodiversity and build resilient ecosystems.',
              path: '/solutions/ecological-restoration',
              icon: <TreeDeciduous className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            },
            {
              name: 'Organic Farming & Soil Health',
              description: 'Improve soil health and promote sustainable, chemical-free farming.',
              path: '/solutions/organic-farming-and-soil-health',
              icon: <Sprout className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            },
            {
              name: 'Sacred & Heritage Landscapes',
              description: 'Design and develop sacred groves, temple landscapes and wellness green spaces.',
              path: '/solutions/sacred-and-heritage-landscapes',
              icon: <Building2 className="text-[#2D7A26]" size={20} strokeWidth={1.5} />
            }
          ]
        }
      ]
    },
    { 
      name: 'Divisions', 
      path: '/divisions',
      description: 'Explore our specialized divisions dedicated to bringing innovation, intelligence, and stewardship to every acre of land.',
      featuredImg: imgGreenHabitat,
      dropdown: [
        { 
          name: 'AGRIQ', 
          subtitle: 'Land Intelligence & Planning', 
          description: 'Advanced land assessment, GIS mapping and master planning for informed decisions.',
          path: '/divisions/agriq', 
          icon: <Globe className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        },
        { 
          name: 'PLANTIQ', 
          subtitle: 'Plantation Development', 
          description: 'High-quality planting material, orchards and scientific establishment systems.',
          path: '/divisions/plantiq', 
          icon: <Sprout className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        },
        { 
          name: 'IRRIQ', 
          subtitle: 'Water & Precision Irrigation', 
          description: 'Smart irrigation, fertigation and automation for maximum water productivity.',
          path: '/divisions/irriq', 
          icon: <Droplets className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        },
        { 
          name: 'SECURE', 
          subtitle: 'Farm Security & Asset Protection', 
          description: 'Surveillance, smart fencing and remote monitoring for total farm protection.',
          path: '/divisions/secure', 
          icon: <ShieldCheck className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        },
        { 
          name: 'ORGANIQ', 
          subtitle: 'Organic Agriculture & Soil Health', 
          description: 'Organic inputs, soil regeneration and sustainable farming for long-term soil vitality.',
          path: '/divisions/organiq', 
          icon: <Leaf className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        },
        { 
          name: 'GREENHABITAT', 
          subtitle: 'Ecological Restoration', 
          description: 'Native landscapes, biodiversity restoration and green infrastructure development.',
          path: '/divisions/greenhabitat-360', 
          icon: <TreeDeciduous className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        },
        { 
          name: 'VEDVAN', 
          subtitle: 'Sacred & Wellness Landscapes', 
          description: 'Sacred groves, temple landscapes and wellness gardens that inspire and sustain.',
          path: '/divisions/vedvan', 
          icon: <Building2 className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        },
        { 
          name: 'FARM360', 
          subtitle: 'Professional Farm Management', 
          description: 'End-to-end farm operations, workforce management and performance improvement.',
          path: '/divisions/farm360', 
          icon: <User className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        },
        { 
          name: 'VIGORIQ', 
          subtitle: 'Crop Health, Diagnostics & Protection', 
          description: 'AI-powered crop monitoring, early detection and science-led crop protection.',
          path: '/divisions/vigoriq', 
          icon: <Cpu className="text-[#2D7A26]" size={24} strokeWidth={1.5} /> 
        }
      ]
    },
    { 
      name: 'Industries', 
      path: '/industries',
      description: 'Solutions tailored to every sector. From agriculture to infrastructure, we partner with diverse industries to create greener, healthier and more resilient environments.',
      featuredImg: imgGreenHabitat,
      dropdown: [
        {
          category: 'AGRICULTURE',
          categoryPath: '/industries/agriculture',
          categoryIcon: <Leaf className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Empowering productive\nand sustainable agriculture.',
          items: [
            { name: 'Orchards', path: '/industries/agriculture#orchards' },
            { name: 'Commercial Farms', path: '/industries/agriculture#commercial-farms' },
            { name: 'Plantation Projects', path: '/industries/agriculture#plantation' },
            { name: 'Vegetable & Horticulture', path: '/industries/agriculture#horticulture' },
            { name: 'Agri Infrastructure', path: '/industries/agriculture#infrastructure' }
          ]
        },
        {
          category: 'INSTITUTIONS',
          categoryPath: '/industries/institutions',
          categoryIcon: <GraduationCap className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Greening campuses\nfor learning and growth.',
          items: [
            { name: 'Universities', path: '/industries/institutions#universities' },
            { name: 'Schools', path: '/industries/institutions#schools' },
            { name: 'Research Campuses', path: '/industries/institutions#research' },
            { name: 'Training Centers', path: '/industries/institutions#training' },
            { name: 'Botanical Gardens', path: '/industries/institutions#botanical' }
          ]
        },
        {
          category: 'GOVERNMENT',
          categoryPath: '/industries/government',
          categoryIcon: <Landmark className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Building resilient and\nsustainable communities.',
          items: [
            { name: 'Forest & Horticulture Dept.', path: '/industries/government#forest' },
            { name: 'Rural Development', path: '/industries/government#rural' },
            { name: 'Smart Village Projects', path: '/industries/government#smart-village' },
            { name: 'Watershed Projects', path: '/industries/government#watershed' },
            { name: 'Ecosystem Restoration', path: '/industries/government#restoration' }
          ]
        },
        {
          category: 'REAL ESTATE & HOSPITALITY',
          categoryPath: '/industries/real-estate',
          categoryIcon: <Building2 className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Creating green spaces that\ninspire and delight.',
          items: [
            { name: 'Townships', path: '/industries/real-estate#townships' },
            { name: 'Resorts & Hotels', path: '/industries/real-estate#resorts' },
            { name: 'Farmhouses & Villas', path: '/industries/real-estate#farmhouses' },
            { name: 'Eco Parks', path: '/industries/real-estate#eco-parks' },
            { name: 'Golf Courses & Clubhouses', path: '/industries/real-estate#golf' }
          ]
        },
        {
          category: 'INDUSTRY & CSR',
          categoryPath: '/industries/industry-csr',
          categoryIcon: <Factory className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Enabling responsible growth\nand environmental impact.',
          items: [
            { name: 'Industrial Landscaping', path: '/industries/industry-csr#landscaping' },
            { name: 'Corporate CSR Projects', path: '/industries/industry-csr#csr' },
            { name: 'ESG & Sustainability', path: '/industries/industry-csr#esg' },
            { name: 'Green Belts', path: '/industries/industry-csr#green-belts' },
            { name: 'Reclamation Projects', path: '/industries/industry-csr#reclamation' }
          ]
        }
      ]
    },
    { 
      name: 'Technology', 
      path: '/technology',
      description: 'Our technology stack combines data, digital innovation and deep domain expertise to deliver scalable, efficient and future-ready land solutions.',
      featuredImg: imgGreenHabitat,
      dropdown: [
        {
          category: 'DATA & INSIGHTS',
          categoryPath: '/technology/data',
          categoryIcon: <TrendingUp className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Leverage data and intelligence\nfor better decisions.',
          items: [
            { name: 'GIS & Remote Sensing', path: '/technology/data#gis-remote-sensing' },
            { name: 'AI-Powered Analysis', path: '/technology/data#ai-powered-analysis' },
            { name: 'Soil & Water Analytics', path: '/technology/data#soil-water-analytics' },
            { name: 'Climate & Risk Modeling', path: '/technology/data#climate-risk-modeling' }
          ]
        },
        {
          category: 'DESIGN & SIMULATE',
          categoryPath: '/technology/design',
          categoryIcon: <Box className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Design smarter with\nsimulation and modeling.',
          items: [
            { name: 'Digital Twin Technology', path: '/technology/design#digital-twin-technology' },
            { name: '3D Landscape Modeling', path: '/technology/design#3d-landscape-modeling' },
            { name: 'Hydrology Simulation', path: '/technology/design#hydrology-simulation' },
            { name: 'Scenario Planning', path: '/technology/design#scenario-planning' }
          ]
        },
        {
          category: 'OPERATE & AUTOMATE',
          categoryPath: '/technology/operate',
          categoryIcon: <Settings className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Automate operations and\nmanage resources efficiently.',
          items: [
            { name: 'IoT & Smart Monitoring', path: '/technology/operate#iot-smart-monitoring' },
            { name: 'Automation & Control', path: '/technology/operate#automation-control' },
            { name: 'Smart Irrigation Solutions', path: '/technology/operate#smart-irrigation-solutions' },
            { name: 'Energy Management', path: '/technology/operate#energy-management' }
          ]
        },
        {
          category: 'MONITOR & IMPROVE',
          categoryPath: '/technology/monitor',
          categoryIcon: <BarChart className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Monitor performance and\ncontinuously improve outcomes.',
          items: [
            { name: 'Real-time Dashboards', path: '/technology/monitor#real-time-dashboards' },
            { name: 'Remote Asset Monitoring', path: '/technology/monitor#remote-asset-monitoring' },
            { name: 'Performance Analytics', path: '/technology/monitor#performance-analytics' },
            { name: 'AI & Machine Learning', path: '/technology/monitor#ai-machine-learning' }
          ]
        },
        {
          category: 'CLOUD & DATA PLATFORM',
          categoryPath: '/technology/cloud',
          categoryIcon: <Cloud className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Secure, scalable and\ncollaborative cloud solutions.',
          items: [
            { name: 'Cloud Infrastructure', path: '/technology/cloud#cloud-infrastructure' },
            { name: 'Data Management', path: '/technology/cloud#data-management' },
            { name: 'API & Integrations', path: '/technology/cloud#api-integrations' },
            { name: 'Data Security', path: '/technology/cloud#data-security' }
          ]
        },
        {
          category: 'MOBILE & FIELD TECH',
          categoryPath: '/technology/mobile',
          categoryIcon: <Smartphone className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Digital tools for field teams\nand real-time operations.',
          items: [
            { name: 'Mobile Applications', path: '/technology/mobile#mobile-applications' },
            { name: 'Field Data Capture', path: '/technology/mobile#field-data-capture' },
            { name: 'Workforce Management', path: '/technology/mobile#workforce-management' },
            { name: 'Offline Sync', path: '/technology/mobile#offline-sync' }
          ]
        },
        {
          category: 'SUSTAINABILITY TECH',
          categoryPath: '/technology/sustainability',
          categoryIcon: <Leaf className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Technology driving regenerative\npractices and circular solutions.',
          items: [
            { name: 'Carbon Accounting', path: '/technology/sustainability#carbon-accounting' },
            { name: 'Biodiversity Monitoring', path: '/technology/sustainability#biodiversity-monitoring' },
            { name: 'Resource Efficiency', path: '/technology/sustainability#resource-efficiency' },
            { name: 'Sustainability Reporting', path: '/technology/sustainability#sustainability-reporting' }
          ]
        },
        {
          category: 'CYBERSECURITY',
          categoryPath: '/technology/cybersecurity',
          categoryIcon: <ShieldCheck className="text-[#2D7A26]" size={20} strokeWidth={1.5} />,
          categoryDesc: 'Enterprise-grade security\nfor data and operations.',
          items: [
            { name: 'Network Security', path: '/technology/cybersecurity#network-security' },
            { name: 'Access Management', path: '/technology/cybersecurity#access-management' },
            { name: 'Threat Detection', path: '/technology/cybersecurity#threat-detection' },
            { name: 'Compliance & Governance', path: '/technology/cybersecurity#compliance-governance' }
          ]
        }
      ]
    },
    { name: 'Projects', path: '/projects' },
    { 
      name: 'Knowledge', 
      path: '/knowledge',
      description: 'Stay informed with expert insights, research and practical knowledge to create sustainable and lasting impact.',
      featuredImg: imgFarmMgmt,
      dropdown: [
        {
          category: 'RESOURCES',
          categoryPath: '/knowledge/resources',
          categoryIcon: <Map className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'Curated resources to\ninform, educate and\ninspire action.',
          items: [
            { name: 'Articles & Insights', path: '/knowledge/resources#articles' },
            { name: 'Case Studies', path: '/knowledge/resources#case-studies' },
            { name: 'White Papers', path: '/knowledge/resources#white-papers' },
            { name: 'E-books & Guides', path: '/knowledge/resources#e-books' },
            { name: 'Reports & Research', path: '/knowledge/resources#reports' },
            { name: 'News & Updates', path: '/knowledge/resources#news' }
          ]
        },
        {
          category: 'LEARN',
          categoryPath: '/knowledge/learn',
          categoryIcon: <Box className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'Expand your knowledge\nwith expert perspectives\nand learning materials.',
          items: [
            { name: 'Webinars & Videos', path: '/knowledge/learn#webinars' },
            { name: 'Learning Center', path: '/knowledge/learn#center' },
            { name: 'Glossary', path: '/knowledge/learn#glossary' },
            { name: 'FAQs', path: '/knowledge/learn#faqs' },
            { name: 'Training Programs', path: '/knowledge/learn#training' },
            { name: 'Certificates', path: '/knowledge/learn#certificates' }
          ]
        },
        {
          category: 'PERSPECTIVES',
          categoryPath: '/knowledge/perspectives',
          categoryIcon: <FileText className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'Thought leadership on\nland, sustainability and\ntechnology.',
          items: [
            { name: 'Expert Opinions', path: '/knowledge/perspectives#expert-opinions' },
            { name: 'Industry Trends', path: '/knowledge/perspectives#industry-trends' },
            { name: 'Sustainability Lens', path: '/knowledge/perspectives#sustainability-lens' },
            { name: 'Policy & Regulations', path: '/knowledge/perspectives#policy-regulations' },
            { name: 'Global Best Practices', path: '/knowledge/perspectives#global-best-practices' },
            { name: 'Future Outlook', path: '/knowledge/perspectives#future-outlook' }
          ]
        },
        {
          category: 'TOOLS & DATA',
          categoryPath: '/knowledge/tools-data',
          categoryIcon: <Download className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'Tools and data to help\nyou plan, analyze and\nmake better decisions.',
          items: [
            { name: 'Data Hub', path: '/knowledge/tools-data#data-hub' },
            { name: 'Calculators', path: '/knowledge/tools-data#calculators' },
            { name: 'Mapping Tools', path: '/knowledge/tools-data#mapping-tools' },
            { name: 'Frameworks', path: '/knowledge/tools-data#frameworks' },
            { name: 'Benchmarks', path: '/knowledge/tools-data#benchmarks' },
            { name: 'Templates', path: '/knowledge/tools-data#templates' }
          ]
        }
      ]
    },
    { 
      name: 'Company', 
      path: '/company',
      description: 'At Kisan, we combine purpose, innovation and people to create sustainable solutions that transform lives and landscapes.',
      featuredImg: imgOrganiq,
      dropdown: [
        {
          category: 'ABOUT KISAN',
          categoryPath: '/company/about',
          categoryIcon: <Building2 className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'Learn about our journey, purpose\nand the values that drive us\nforward.',
          items: [
            { name: 'Our Story', path: '/company/about#our-story' },
            { name: 'Vision & Mission', path: '/company/about#vision-mission' },
            { name: 'Core Values', path: '/company/about#core-values' },
            { name: 'Leadership Message', path: '/company/about#leadership-message' }
          ]
        },
        {
          category: 'LEADERSHIP',
          categoryPath: '/company/leadership',
          categoryIcon: <Users className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'Meet the leaders steering\ninnovation, growth and\nimpact at Kisan.',
          items: [
            { name: 'Board of Directors', path: '/company/leadership#board-of-directors' },
            { name: 'Leadership Team', path: '/company/leadership#leadership-team' },
            { name: 'Advisory Board', path: '/company/leadership#advisory-board' },
            { name: 'Leadership Philosophy', path: '/company/leadership#leadership-philosophy' }
          ]
        },
        {
          category: 'GLOBAL PRESENCE',
          categoryPath: '/company/global',
          categoryIcon: <Globe className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'A strong presence across\nIndia and beyond,\ndelivering local impact.',
          items: [
            { name: 'Our Presence', path: '/company/global#our-presence' },
            { name: 'Global Footprint', path: '/company/global#global-footprint' },
            { name: 'International Projects', path: '/company/global#international-projects' },
            { name: 'Partners & Alliances', path: '/company/global#partners-alliances' }
          ]
        },
        {
          category: 'NEWSROOM',
          categoryPath: '/company/newsroom',
          categoryIcon: <FileText className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'Stay updated with the\nlatest news, announcements\nand media coverage.',
          items: [
            { name: 'Press Releases', path: '/company/newsroom#press-releases' },
            { name: 'News & Updates', path: '/company/newsroom#news-updates' },
            { name: 'Media Coverage', path: '/company/newsroom#media-coverage' },
            { name: 'Events', path: '/company/newsroom#events' }
          ]
        },
        {
          category: 'CAREERS',
          categoryPath: '/careers',
          categoryIcon: <Briefcase className="text-[#2D7A26]" size={24} strokeWidth={1.5} />,
          categoryDesc: 'Build a meaningful career\nand grow with a purpose-\ndriven team.',
          items: [
            { name: 'Life at Kisan', path: '/careers#life-at-kisan' },
            { name: 'Learning & Development', path: '/careers#learning-development' },
            { name: 'Diversity & Inclusion', path: '/careers#diversity-inclusion' },
            { name: 'Open Positions', path: '/careers#open-positions' }
          ]
        }
      ]
    },
  ];

  return (
    <div className="fixed top-0 left-0 right-0 z-50 transition-all duration-500">
      {/* Top Utility Bar */}
      <div className={`bg-[#0a381f] text-white flex items-center justify-between px-6 lg:px-14 overflow-hidden transition-all duration-300 ${isScrolled ? 'h-0 opacity-0' : 'h-[36px] opacity-100'}`}>
        <div className="flex items-center text-[11px] sm:text-[12px] md:text-[13px] font-medium tracking-wide">
          A <span className="text-[#96c11f] font-bold mx-1">VBR</span> Group Company
        </div>
        <div className="flex items-center gap-3 lg:gap-4 text-[11px] sm:text-[12px] lg:text-[13px] font-medium">
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/sustainability" className="flex items-center gap-1.5 hover:text-gray-300 transition-colors">
              <Leaf size={14} />
              <span>Sustainability</span>
            </Link>
            <span className="text-white/30">|</span>
            <Link to="/careers" className="flex items-center gap-1.5 hover:text-gray-300 transition-colors">
              <Briefcase size={14} />
              <span>Careers</span>
            </Link>
            <span className="text-white/30">|</span>
          </div>
          
          <div className="notranslate flex items-center gap-1.5 sm:gap-2">
            <div onClick={() => changeLanguage('en')} className="flex items-center gap-1 hover:text-gray-300 transition-colors cursor-pointer">
              <Globe size={12} className="lg:w-[14px] lg:h-[14px]" />
              <span>EN</span>
            </div>
            <span className="text-white/30">|</span>
            <div onClick={() => changeLanguage('te')} className="hover:text-gray-300 transition-colors cursor-pointer">తెలుగు</div>
            <span className="text-white/30">|</span>
            <div onClick={() => changeLanguage('hi')} className="hover:text-gray-300 transition-colors cursor-pointer">हिन्दी</div>
          </div>
          
          <a href="tel:+919492023333" className="hidden lg:flex items-center gap-1.5 ml-2 font-bold hover:text-gray-300 transition-colors">
            <Phone size={14} />
            <span>+91 94920 23333</span>
          </a>
        </div>
      </div>

      <header 
        className={`bg-white/95 backdrop-blur-md border-b border-gray-100 transition-all duration-500 mx-auto w-full py-2 lg:py-3 px-6 lg:px-10 ${isScrolled ? 'shadow-premium' : 'shadow-sm'} relative`}
      >
        <div className="flex items-center justify-between transition-all duration-500">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center shrink-0"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img 
              src={logo} 
              alt="Kisan Plant Technologies" 
              className={`transition-all duration-500 h-auto
              ${isScrolled ? 'w-[150px] lg:w-[190px]' : 'w-[170px] lg:w-[230px]'}`} 
             loading="eager" fetchpriority="high" decoding="sync" />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex flex-1 justify-center items-center gap-4 xl:gap-8">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path || (link.path.startsWith('/#') && location.hash === link.path.substring(1));
              
              if (link.dropdown) {
                const isDropdownOpen = activeDropdown === link.name;
                return (
                  <div 
                    key={link.name}
                    className="group static h-full py-2"
                    onMouseEnter={() => setActiveDropdown(link.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center gap-1 text-[14px] font-bold tracking-normal transition-colors duration-300
                      ${isActive || isDropdownOpen ? 'text-primary-green' : 'text-slate-700'} 
                      hover:text-primary-green`}
                    >
                      {link.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </Link>

                    {/* Mega Menu Dropdown */}
                    {link.dropdown.length > 0 && (
                      <div className={`absolute left-0 right-0 top-full pt-1 transition-all duration-300 ${isDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'}`}>
                        <div className={`bg-white shadow-premium border-t border-gray-100 overflow-hidden mx-auto w-full max-w-none`} onClick={() => setActiveDropdown(null)}>
                          
                          {link.name === 'Solutions' ? (
                            <div className="flex flex-col lg:flex-row h-[520px] bg-[#0a110a] text-white overflow-hidden">
                              {/* Left Column - Solutions Grid */}
                              <div className="lg:w-[70%] pt-4 pb-2 px-6 flex flex-col">
                                <div className="flex divide-x divide-[#1a231a] flex-1">
                                  {link.dropdown.map((cat, idx) => (
                                    <div key={idx} className="flex-1 px-5 first:pl-0 last:pr-0 flex flex-col gap-4">
                                      {/* Category Header */}
                                      <div className="flex items-start gap-3 mb-2 border-b border-[#1a231a] pb-4">
                                        <div className="w-10 h-10 rounded-full border border-[#2a362a] flex items-center justify-center shrink-0">
                                          {cat.categoryIcon}
                                        </div>
                                        <div>
                                          <h4 className="text-[14px] font-bold text-[#2D7A26] tracking-wide mb-1">
                                            {cat.category}
                                          </h4>
                                          <p className="text-[12px] text-gray-400 font-medium whitespace-pre-line leading-relaxed">
                                            {cat.categoryDesc}
                                          </p>
                                        </div>
                                      </div>
                                      
                                      {/* Category Items */}
                                      <div className="flex flex-col gap-1 mt-2">
                                        {cat.items.map((item, itemIdx) => (
                                          <Link
                                            key={itemIdx}
                                            to={item.path}
                                            className="group flex flex-col gap-1.5 p-3 rounded-xl hover:bg-[#1a231a] border border-transparent hover:border-[#2D7A26] transition-colors duration-300 relative -mx-3"
                                          >
                                            <div className="flex items-start gap-3">
                                              <div className="w-10 h-10 rounded-full border border-[#2a362a] flex items-center justify-center shrink-0 group-hover:border-[#2D7A26] transition-colors">
                                                {item.icon}
                                              </div>
                                              <div className="pr-4">
                                                <h5 className="text-[13px] font-bold text-white tracking-wide">
                                                  {item.name}
                                                </h5>
                                                <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
                                                  {item.description}
                                                </p>
                                              </div>
                                            </div>
                                            <ChevronRight className="absolute right-2 top-1/2 -translate-y-1/2 text-[#2D7A26] opacity-0 group-hover:opacity-100 transition-opacity" size={16} />
                                          </Link>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Right Column - Featured Image */}
                              <div className="lg:w-[30%] relative group overflow-hidden">
                                <img src={link.featuredImg} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Solutions" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a110a] via-[#0a110a]/80 to-transparent"></div>
                                
                                <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col items-start z-10">
                                  <span className="text-[#2D7A26] text-[11px] font-bold tracking-widest uppercase mb-3">INTEGRATED SOLUTIONS</span>
                                  <h3 className="text-white font-extrabold text-3xl mb-4 leading-tight">
                                    End-to-end land<br/>transformation.
                                  </h3>
                                  <p className="text-gray-300 text-sm leading-relaxed mb-8">
                                    From planning to regeneration, our integrated solutions combine science, technology and experience to deliver measurable and lasting impact.
                                  </p>
                                  <Link 
                                    to={link.path} 
                                    className="inline-flex items-center gap-1 text-[14px] font-medium text-white bg-[#2D7A26] border border-[#2D7A26] px-6 py-3 rounded-md hover:bg-[#1f5c18] hover:border-[#1f5c18] transition-colors w-fit shadow-md"
                                  >
                                    Explore All Solutions <ArrowRight size={14} />
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ) : link.name === 'Divisions' ? (
                            <div className="flex flex-col lg:flex-row h-[520px] bg-[#0a110a] text-white overflow-hidden">
                              {/* Left Column - Divisions Grid */}
                              <div className="lg:w-[70%] pt-4 pb-2 px-6 flex flex-col justify-between">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
                                  {link.dropdown.map((subItem) => (
                                    <Link
                                      key={subItem.name}
                                      to={subItem.path}
                                      className="group flex flex-col gap-1.5 p-3 rounded-xl hover:bg-[#1a231a] border border-transparent hover:border-[#2D7A26] transition-colors duration-300 relative"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full border border-[#2a362a] flex items-center justify-center shrink-0 group-hover:border-[#2D7A26] transition-colors">
                                          {subItem.icon}
                                        </div>
                                        <div>
                                          <h4 className="text-[15px] font-bold text-white tracking-wide">
                                            {subItem.name}
                                          </h4>
                                          <p className="text-[12px] text-gray-400 font-medium">
                                            {subItem.subtitle}
                                          </p>
                                        </div>
                                      </div>
                                      <p className="text-[12px] text-gray-500 mt-2 leading-relaxed">
                                        {subItem.description}
                                      </p>
                                      <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2D7A26] opacity-0 group-hover:opacity-100 transition-opacity" size={18} />
                                    </Link>
                                  ))}
                                </div>
                                

                              </div>

                              {/* Right Column - Featured Image */}
                              <div className="lg:w-[30%] relative group overflow-hidden">
                                <img src={link.featuredImg} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Divisions" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a110a] via-[#0a110a]/80 to-transparent"></div>
                                
                                <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col items-start z-10">
                                  <span className="text-[#2D7A26] text-[11px] font-bold tracking-widest uppercase mb-3">OUR DIVISIONS</span>
                                  <h3 className="text-white font-extrabold text-3xl mb-4 leading-tight">
                                    Specialized expertise.<br/>Stronger together.
                                  </h3>
                                  <p className="text-gray-300 text-sm leading-relaxed mb-8">
                                    Nine specialized divisions working in harmony to deliver end-to-end land transformation and lasting impact.
                                  </p>
                                  <Link 
                                    to={link.path} 
                                    className="inline-flex items-center gap-1 text-[14px] font-medium text-white bg-[#2D7A26] border border-[#2D7A26] px-6 py-3 rounded-md hover:bg-[#1f5c18] hover:border-[#1f5c18] transition-colors w-fit shadow-md"
                                  >
                                    Explore the Kisan Ecosystem <ArrowRight size={16} />
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ) : link.name === 'Industries' ? (
                            <div className="flex flex-col lg:flex-row h-[520px] bg-[#0a110a] text-white overflow-hidden">
                              {/* Left Column - Industries Grid */}
                              <div className="lg:w-[70%] flex flex-col justify-between pt-4 pb-2 px-6">
                                <div className="flex flex-col pr-4">
                                  {/* Top Row - 3 Categories */}
                                  <div className="flex">
                                    {link.dropdown.slice(0, 3).map((cat, idx) => (
                                      <div key={idx} className={`w-1/3 px-4 first:pl-0 flex flex-col gap-1 ${idx > 0 ? 'border-l border-[#1a231a]' : ''}`}>
                                        <Link to={cat.categoryPath} className="group flex items-start gap-3 mb-2 border-b border-[#1a231a] pt-3 pb-3 h-auto hover:bg-[#1a231a] -mx-2 px-2 rounded-lg transition-colors">
                                          <div className="w-10 h-10 rounded-full border border-[#2a362a] flex items-center justify-center shrink-0 group-hover:border-[#2D7A26] transition-colors">
                                            {cat.categoryIcon}
                                          </div>
                                          <div>
                                            <h4 className="text-[13px] font-bold text-white tracking-wide mb-1 uppercase group-hover:text-[#2D7A26] transition-colors">
                                              {cat.category}
                                            </h4>
                                            <p className="text-[11px] text-gray-400 font-medium whitespace-pre-line leading-relaxed group-hover:text-gray-300 transition-colors">
                                              {cat.categoryDesc}
                                            </p>
                                          </div>
                                        </Link>
                                        <div className="flex flex-col gap-[4px]">
                                          {cat.items.map((item, itemIdx) => (
                                            <Link
                                              key={itemIdx}
                                              to={item.path}
                                              className="group flex items-center justify-between py-[4px] px-2 -mx-2 rounded-lg hover:bg-[#1a231a] transition-colors"
                                            >
                                              <span className="text-[12px] text-gray-300 group-hover:text-white transition-colors">{item.name}</span>
                                              <ChevronRight className="text-[#2D7A26] opacity-0 group-hover:opacity-100 transition-opacity" size={14} />
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Divider */}
                                  <div className="w-full h-px bg-[#1a231a] my-3"></div>

                                  {/* Bottom Row - 2 Categories */}
                                  <div className="flex">
                                    {link.dropdown.slice(3, 5).map((cat, idx) => (
                                      <div key={idx} className={`w-1/2 px-4 first:pl-0 flex flex-col gap-1 ${idx > 0 ? 'border-l border-[#1a231a]' : ''}`}>
                                        <Link to={cat.categoryPath} className="group flex items-start gap-3 mb-2 border-b border-[#1a231a] pt-3 pb-3 h-auto hover:bg-[#1a231a] -mx-2 px-2 rounded-lg transition-colors">
                                          <div className="w-10 h-10 rounded-full border border-[#2a362a] flex items-center justify-center shrink-0 group-hover:border-[#2D7A26] transition-colors">
                                            {cat.categoryIcon}
                                          </div>
                                          <div>
                                            <h4 className="text-[13px] font-bold text-white tracking-wide mb-1 uppercase group-hover:text-[#2D7A26] transition-colors">
                                              {cat.category}
                                            </h4>
                                            <p className="text-[11px] text-gray-400 font-medium whitespace-pre-line leading-relaxed group-hover:text-gray-300 transition-colors">
                                              {cat.categoryDesc}
                                            </p>
                                          </div>
                                        </Link>
                                        <div className="flex flex-col gap-[4px]">
                                          {cat.items.map((item, itemIdx) => (
                                            <Link
                                              key={itemIdx}
                                              to={item.path}
                                              className="group flex items-center justify-between py-[4px] px-2 -mx-2 rounded-lg hover:bg-[#1a231a] transition-colors"
                                            >
                                              <span className="text-[12px] text-gray-300 group-hover:text-white transition-colors">{item.name}</span>
                                              <ChevronRight className="text-[#2D7A26] opacity-0 group-hover:opacity-100 transition-opacity" size={14} />
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Right Column - Featured Image */}
                              <div className="lg:w-[30%] relative group overflow-hidden">
                                <img src={link.featuredImg} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Industries" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a110a] via-[#0a110a]/80 to-transparent"></div>
                                
                                <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col items-start z-10">
                                  <span className="text-[#2D7A26] text-[11px] font-bold tracking-widest uppercase mb-3">INDUSTRIES WE SERVE</span>
                                  <h3 className="text-white font-extrabold text-3xl mb-4 leading-tight">
                                    Solutions tailored<br/>to every sector.
                                  </h3>
                                  <p className="text-gray-300 text-sm leading-relaxed mb-8">
                                    From agriculture to infrastructure, we partner with diverse industries to create greener, healthier and more resilient environments.
                                  </p>
                                  <Link 
                                    to={link.path} 
                                    className="inline-flex items-center gap-1 text-[14px] font-medium text-white bg-[#2D7A26] border border-[#2D7A26] px-6 py-3 rounded-md hover:bg-[#1f5c18] hover:border-[#1f5c18] transition-colors w-fit shadow-md"
                                  >
                                    Explore Industries <ArrowRight size={14} />
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ) : link.name === 'Technology' ? (
                            <div className="flex flex-col lg:flex-row h-[520px] bg-[#0a110a] text-white overflow-hidden">
                              {/* Left Column - Tech Grid */}
                              <div className="lg:w-[75%] flex flex-col justify-between pt-4 pb-2 px-6">
                                <div className="flex flex-col pr-4">
                                  {/* Top Row - 4 Categories */}
                                  <div className="flex">
                                    {link.dropdown.slice(0, 4).map((cat, idx) => (
                                      <div key={idx} className={`w-1/4 px-4 first:pl-0 flex flex-col gap-1 ${idx > 0 ? 'border-l border-[#1a231a]' : ''}`}>
                                        <Link to={cat.categoryPath || '#'} className="group flex items-start gap-3 mb-2 border-b border-[#1a231a] pb-3 pt-2 -mx-2 px-2 rounded-lg hover:bg-[#1a231a] h-[88px] transition-colors">
                                          <div className="w-10 h-10 rounded-full border border-[#2a362a] flex items-center justify-center shrink-0 group-hover:border-[#2D7A26] transition-colors">
                                            {cat.categoryIcon}
                                          </div>
                                          <div>
                                            <h4 className="text-[12px] font-bold text-white tracking-wide mb-1 uppercase group-hover:text-[#2D7A26] transition-colors">
                                              {cat.category}
                                            </h4>
                                            <p className="text-[10px] text-gray-400 font-medium whitespace-pre-line leading-snug group-hover:text-gray-300 transition-colors">
                                              {cat.categoryDesc}
                                            </p>
                                          </div>
                                        </Link>
                                        <div className="flex flex-col gap-[4px]">
                                          {cat.items.map((item, itemIdx) => (
                                            <Link
                                              key={itemIdx}
                                              to={item.path}
                                              className="group flex items-center justify-between py-[4px] px-2 -mx-2 rounded-lg hover:bg-[#1a231a] transition-colors"
                                            >
                                              <span className="text-[11px] text-gray-300 group-hover:text-white transition-colors">{item.name}</span>
                                              <ChevronRight className="text-[#2D7A26] opacity-0 group-hover:opacity-100 transition-opacity" size={14} />
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Divider */}
                                  <div className="w-full h-px bg-[#1a231a] my-3"></div>

                                  {/* Bottom Row - 4 Categories */}
                                  <div className="flex">
                                    {link.dropdown.slice(4, 8).map((cat, idx) => (
                                      <div key={idx} className={`w-1/4 px-4 first:pl-0 flex flex-col gap-1 ${idx > 0 ? 'border-l border-[#1a231a]' : ''}`}>
                                        <Link to={cat.categoryPath || '#'} className="group flex items-start gap-3 mb-2 border-b border-[#1a231a] pb-3 pt-2 -mx-2 px-2 rounded-lg hover:bg-[#1a231a] h-[88px] transition-colors">
                                          <div className="w-10 h-10 rounded-full border border-[#2a362a] flex items-center justify-center shrink-0 group-hover:border-[#2D7A26] transition-colors">
                                            {cat.categoryIcon}
                                          </div>
                                          <div>
                                            <h4 className="text-[12px] font-bold text-white tracking-wide mb-1 uppercase group-hover:text-[#2D7A26] transition-colors">
                                              {cat.category}
                                            </h4>
                                            <p className="text-[10px] text-gray-400 font-medium whitespace-pre-line leading-snug group-hover:text-gray-300 transition-colors">
                                              {cat.categoryDesc}
                                            </p>
                                          </div>
                                        </Link>
                                        <div className="flex flex-col gap-[4px]">
                                          {cat.items.map((item, itemIdx) => (
                                            <Link
                                              key={itemIdx}
                                              to={item.path}
                                              className="group flex items-center justify-between py-[4px] px-2 -mx-2 rounded-lg hover:bg-[#1a231a] transition-colors"
                                            >
                                              <span className="text-[11px] text-gray-300 group-hover:text-white transition-colors">{item.name}</span>
                                              <ChevronRight className="text-[#2D7A26] opacity-0 group-hover:opacity-100 transition-opacity" size={14} />
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Right Column - Featured Image */}
                              <div className="lg:w-[25%] relative group overflow-hidden">
                                <img src={link.featuredImg} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Technology" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a110a] via-[#0a110a]/80 to-transparent"></div>
                                
                                <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col items-start z-10">
                                  <span className="text-[#2D7A26] text-[10px] font-bold tracking-widest uppercase mb-3">TECHNOLOGY THAT TRANSFORMS</span>
                                  <h3 className="text-white font-extrabold text-2xl mb-4 leading-tight">
                                    Intelligent solutions.<br/>Sustainable impact.
                                  </h3>
                                  <p className="text-gray-300 text-[12px] leading-relaxed mb-6">
                                    {link.description}
                                  </p>
                                  <Link 
                                    to={link.path} 
                                    className="inline-flex items-center gap-1 text-[13px] font-medium text-white bg-[#2D7A26] border border-[#2D7A26] px-5 py-2.5 rounded-md hover:bg-[#1f5c18] hover:border-[#1f5c18] transition-colors w-fit shadow-md"
                                  >
                                    Explore Technologies <ArrowRight size={14} />
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ) : link.name === 'Knowledge' ? (
                            <div className="flex flex-col lg:flex-row h-[520px] bg-[#0a110a] text-white overflow-hidden">
                              {/* Left Column - Knowledge Grid */}
                              <div className="lg:w-[75%] flex flex-col justify-between pt-10 pb-8 px-6">
                                <div className="flex flex-col pr-4 h-full">
                                  {/* 4 Categories */}
                                  <div className="flex h-full">
                                    {link.dropdown.map((cat, idx) => (
                                      <div key={idx} className={`w-1/4 px-6 flex flex-col gap-4 ${idx > 0 ? 'border-l border-[#1a231a]' : ''}`}>
                                        <Link to={cat.categoryPath || '#'} className="group flex flex-col items-start gap-3 mb-2 border-b border-[#1a231a] pb-6 pt-2 rounded-lg hover:bg-[#1a231a] -mx-4 px-4 transition-colors">
                                          <div className="w-12 h-12 rounded-full border border-[#2a362a] flex items-center justify-center shrink-0 group-hover:border-[#2D7A26] transition-colors">
                                            {cat.categoryIcon}
                                          </div>
                                          <div>
                                            <h4 className="text-[14px] font-bold text-white tracking-wide mb-2 uppercase group-hover:text-[#2D7A26] transition-colors">
                                              {cat.category}
                                            </h4>
                                            <p className="text-[12px] text-gray-400 font-medium whitespace-pre-line leading-relaxed group-hover:text-gray-300 transition-colors">
                                              {cat.categoryDesc}
                                            </p>
                                          </div>
                                        </Link>
                                        <div className="flex flex-col gap-[8px] flex-1">
                                          {cat.items.map((item, itemIdx) => (
                                            <Link
                                              key={itemIdx}
                                              to={item.path}
                                              className="group flex items-center justify-between py-[6px] px-4 -mx-4 rounded-lg hover:bg-[#1a231a] transition-colors"
                                            >
                                              <span className="text-[13px] text-gray-300 group-hover:text-white transition-colors">{item.name}</span>
                                              <ChevronRight className="text-[#2D7A26] opacity-0 group-hover:opacity-100 transition-opacity" size={16} />
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Right Column - Featured Image */}
                              <div className="lg:w-[25%] relative group overflow-hidden">
                                <img src={link.featuredImg} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Knowledge" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a110a] via-[#0a110a]/80 to-transparent"></div>
                                
                                <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col items-start z-10">
                                  <span className="text-[#2D7A26] text-[11px] font-bold tracking-widest uppercase mb-3">KNOWLEDGE THAT EMPOWERS</span>
                                  <h3 className="text-white font-extrabold text-3xl mb-4 leading-tight">
                                    Insights that drive<br/>better outcomes.
                                  </h3>
                                  <p className="text-gray-300 text-sm leading-relaxed mb-8">
                                    {link.description}
                                  </p>
                                  <Link 
                                    to={link.path} 
                                    className="inline-flex items-center gap-1 text-[14px] font-medium text-white bg-[#2D7A26] border border-[#2D7A26] px-6 py-3 rounded-md hover:bg-[#1f5c18] hover:border-[#1f5c18] transition-colors w-fit shadow-md"
                                  >
                                    Explore Knowledge Hub <ArrowRight size={14} />
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ) : link.name === 'Company' ? (
                            <div className="flex flex-col lg:flex-row h-[520px] bg-[#0a110a] text-white overflow-hidden">
                              {/* Left Column - Company Grid */}
                              <div className="lg:w-[75%] flex flex-col justify-between pt-10 pb-8 px-6">
                                <div className="flex flex-col pr-2 h-full">
                                  {/* 5 Categories */}
                                  <div className="flex h-full">
                                    {link.dropdown.map((cat, idx) => (
                                      <div key={idx} className={`w-1/5 px-4 flex flex-col gap-3 ${idx > 0 ? 'border-l border-[#1a231a]' : ''}`}>
                                        <Link to={cat.categoryPath || '#'} className="group flex flex-col items-start gap-3 mb-2 border-b border-[#1a231a] pb-4 pt-1 rounded-lg hover:bg-[#1a231a] -mx-2 px-2 transition-colors h-[160px]">
                                          <div className="w-12 h-12 rounded-lg border border-[#2a362a] flex items-center justify-center shrink-0 group-hover:border-[#2D7A26] transition-colors">
                                            {cat.categoryIcon}
                                          </div>
                                          <div>
                                            <h4 className="text-[13px] font-bold text-white tracking-wide mb-1 uppercase group-hover:text-[#2D7A26] transition-colors">
                                              {cat.category}
                                            </h4>
                                            <p className="text-[11px] text-gray-400 font-medium whitespace-pre-line leading-relaxed group-hover:text-gray-300 transition-colors">
                                              {cat.categoryDesc}
                                            </p>
                                          </div>
                                        </Link>
                                        <div className="flex flex-col justify-between flex-1 py-4">
                                          {cat.items.map((item, itemIdx) => (
                                            <Link
                                              key={itemIdx}
                                              to={item.path}
                                              className="group flex items-center justify-between py-2 px-2 -mx-2 rounded-lg hover:bg-[#1a231a] transition-colors"
                                            >
                                              <span className="text-[13px] text-gray-300 group-hover:text-white transition-colors">{item.name}</span>
                                              <ChevronRight className="text-[#2D7A26] opacity-0 group-hover:opacity-100 transition-opacity" size={16} />
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Right Column - Featured Image */}
                              <div className="lg:w-[25%] relative group overflow-hidden">
                                <img src={link.featuredImg} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Company" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a110a] via-[#0a110a]/80 to-transparent"></div>
                                
                                <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col items-start z-10">
                                  <span className="text-[#2D7A26] text-[10px] font-bold tracking-widest uppercase mb-3">BUILT ON PURPOSE. DRIVEN BY IMPACT.</span>
                                  <h3 className="text-white font-extrabold text-2xl mb-4 leading-tight">
                                    Engineering a better<br/>tomorrow, together.
                                  </h3>
                                  <p className="text-gray-300 text-[12px] leading-relaxed mb-6">
                                    {link.description}
                                  </p>
                                  <Link 
                                    to={link.path} 
                                    className="inline-flex items-center gap-1 text-[13px] font-medium text-white bg-[#2D7A26] border border-[#2D7A26] px-5 py-2.5 rounded-md hover:bg-[#1f5c18] hover:border-[#1f5c18] transition-colors w-fit shadow-md"
                                  >
                                    Discover Our Journey <ArrowRight size={14} />
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col lg:flex-row h-[520px] overflow-hidden">
                              {/* Left Column - Featured Content */}
                              <div className="lg:w-1/3 bg-white p-8 lg:p-12 flex flex-col justify-center relative overflow-hidden group">
                                {/* Image Background */}
                                <div className="absolute inset-0 opacity-25 transition-opacity duration-500 group-hover:opacity-35">
                                  <img src={link.featuredImg || logo} className="w-full h-full object-cover" alt="" />
                                </div>
                                {/* White Gradient Fade */}
                                <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-white/80 to-white"></div>
                                
                                <div className="relative z-10">
                                  <h3 className="text-primary-green font-extrabold tracking-widest uppercase text-3xl mb-5">{link.name}</h3>
                                  <p className="text-slate-600 font-normal text-base leading-relaxed mb-8 max-w-sm">
                                    {link.description}
                                  </p>
                                  <Link 
                                    to={link.path} 
                                    className="inline-flex items-center gap-1 text-[15px] font-bold text-white bg-primary-green px-6 py-3 rounded-full hover:bg-primary-green-dark transition-colors shadow-sm hover:shadow-md w-fit"
                                  >
                                    Explore All {link.name} &rarr;
                                  </Link>
                                </div>
                              </div>

                              {/* Right Columns - Submenu Items */}
                              <div className="lg:w-2/3 p-8 lg:p-10 overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                  {link.dropdown.map((subItem) => (
                                    <Link
                                      key={subItem.name}
                                      to={subItem.path}
                                      className="group flex items-center gap-4 p-2 rounded-xl hover:bg-primary-green-soft transition-colors duration-300"
                                    >
                                      {subItem.img && (
                                        <div className="w-14 h-14 shrink-0 overflow-hidden rounded-lg bg-gray-100 relative shadow-sm">
                                          <img 
                                            src={subItem.img} 
                                            alt={subItem.name} 
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                          />
                                        </div>
                                      )}
                                      <div className="flex-1 flex items-center gap-3">
                                        {subItem.icon && (
                                          <span className="text-primary-green opacity-80 group-hover:opacity-100 transition-opacity bg-primary-green/10 p-2 rounded-xl shrink-0">
                                            {subItem.icon}
                                          </span>
                                        )}
                                        <span className="text-[13px] text-slate-800 group-hover:text-primary-green transition-colors font-bold leading-tight tracking-wide">
                                          {subItem.name}
                                        </span>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-[14px] font-bold tracking-normal transition-colors duration-300
                  ${isActive ? 'text-primary-green' : 'text-slate-700'} 
                  hover:text-primary-green`}
                >
                  {link.name}
                </Link>
              );
            })}
          </nav>
          
          {/* Right Actions */}
          <div className="hidden lg:flex items-center gapt-4 pb-2 px-6 shrink-0">
            <Link 
              to="/contact" 
              className="flex items-center gap-1 bg-[#2d7a26] text-white px-5 py-2.5 rounded-md font-medium text-[14px] hover:bg-[#1f5c18] transition-colors shadow-sm"
            >
              Get In Touch
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden flex flex-col gap-1.5 p-2" 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            <span className={`w-6 h-0.5 bg-black transition-all ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`w-6 h-0.5 bg-black transition-all ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`w-6 h-0.5 bg-black transition-all ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

      {/* Mobile Menu Content */}
      {isMobileMenuOpen && (
        <div className={`lg:hidden relative z-50 bg-white py-6 flex flex-col gap-4 animate-fade-in max-h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar ${isScrolled ? 'border-t border-gray-100' : 'mt-4 rounded-3xl border-t border-gray-50 shadow-lg'}`}>
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <div key={link.name}>
                  {link.dropdown ? (
                    <button
                      className={`w-full text-left text-sm font-bold tracking-normal py-2 border-b border-gray-50 px-6 flex justify-between items-center
                      ${isActive ? 'text-primary-green' : 'text-slate-800'}`}
                      onClick={() => setMobileActiveDropdown(mobileActiveDropdown === link.name ? null : link.name)}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobileActiveDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`text-sm font-bold tracking-normal py-2 border-b border-gray-50 px-6 flex justify-between items-center
                      ${isActive ? 'text-primary-green' : 'text-slate-800'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  )}
                  {link.dropdown && link.dropdown.length > 0 && mobileActiveDropdown === link.name && (
                    <div className="bg-[#f8f9fa] px-8 py-2">
                      {link.dropdown.map((sub, idx) => {
                        if (sub.category) {
                          return (
                            <div key={idx} className="py-2">
                               <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 mt-2">{sub.category}</div>
                               {sub.items && sub.items.map(item => (
                                 <Link
                                   key={item.name}
                                   to={item.path || (sub.categoryPath ? sub.categoryPath : '#')}
                                   className="flex items-center gap-3 py-2 text-xs font-bold text-black tracking-wide"
                                   onClick={() => setIsMobileMenuOpen(false)}
                                 >
                                   {item.icon && (
                                     <span className="text-primary-green">
                                       {item.icon}
                                     </span>
                                   )}
                                   {item.name}
                                 </Link>
                               ))}
                            </div>
                          );
                        } else {
                          return (
                            <Link
                              key={sub.name || idx}
                              to={sub.path || '#'}
                              className="flex items-center gap-3 py-2 text-xs font-bold text-black tracking-wide"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {sub.icon && (
                                <span className="text-primary-green">
                                  {sub.icon}
                                </span>
                              )}
                              {sub.number && (
                                <span className="text-primary-green">
                                  {sub.number}
                                </span>
                              )}
                              {sub.name}
                            </Link>
                          );
                        }
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Mobile Actions */}
            <div className="flex flex-col gap-3 px-6 mt-4 pt-4 border-t border-gray-100">
              <Link 
                to="/contact" 
                className="flex items-center justify-center gap-1 bg-[#2d7a26] text-white px-5 py-3 rounded-md font-medium text-[14px]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Request Land Assessment
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Translation Loader Overlay */}
      {isTranslating && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white">
          <style>{`
            @keyframes dotFade {
              0%, 80%, 100% { opacity: 0; }
              40% { opacity: 1; }
            }
            .loading-dot {
              animation: dotFade 1.2s infinite ease-in-out both;
            }
            .loading-dot:nth-child(1) { animation-delay: -0.32s; }
            .loading-dot:nth-child(2) { animation-delay: -0.16s; }
            .loading-dot:nth-child(3) { animation-delay: 0s; }
          `}</style>
          <img src={logo} alt="Kisan Plant" className="h-20 mb-8 animate-pulse" />
          <div className="flex items-center gap-2.5">
            <div className="w-3 h-3 bg-[#2D7A26] rounded-full loading-dot"></div>
            <div className="w-3 h-3 bg-[#2D7A26] rounded-full loading-dot"></div>
            <div className="w-3 h-3 bg-[#2D7A26] rounded-full loading-dot"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;

